import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import { useToast } from '../ui/Toast';
import { toCsv } from '../../utils/csv.js';

function downloadCsv(filename, header, rows) {
  const csv = toCsv(header, rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ── Client-side "closest catalogue item" guesses for New Item Candidates ────
// Deliberately separate from (and looser than) the server's auto-match,
// which only ever surfaces >=0.5 Jaccard as a reviewable "Suggested match".
// This runs only when a reviewer opens the link picker for one specific
// cluster, so it can afford to show near-misses too — e.g. a bare "Grade 1"
// against "...Resource for Network Engineer (Grade 1)" scores low on pure
// Jaccard purely because of the length difference, even though every token
// it has is a real match. Still always a manual click to confirm.
function fmNormalize(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}
function fmTokens(s) {
  return new Set(fmNormalize(s).split(' ').filter(w => w.length > 1));
}
function fmJaccard(a, b) {
  if (!a.size || !b.size) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  return inter / (a.size + b.size - inter);
}
// tokensMap is optional: a precomputed Map(item.id -> Set) so scanning the
// same catalog/bands list across thousands of clusters (the high-confidence
// scan below) doesn't re-tokenize every item's description from scratch on
// every single cluster — only the on-demand manual picker skips it, since
// that only ever runs for one cluster at a time.
function topCatalogGuesses(query, catalog, limit = 6, tokensMap = null) {
  const qTokens = fmTokens(query);
  if (!qTokens.size) return [];
  return catalog
    .map(item => ({ item, score: fmJaccard(qTokens, tokensMap ? (tokensMap.get(item.id) || new Set()) : fmTokens(item.description)) }))
    .filter(g => g.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-6', label: 'Sonnet 4.6 (default — reliably finds resource role names)' },
  { value: 'claude-haiku-4-5',  label: 'Haiku 4.5 (cheaper/faster — often skips the resource role name)' },
];

const CATEGORY_OPTIONS = ['Hardware', 'Network Install', 'Copper Install', 'Fibre Install', 'Containment', 'Resource'];

// Matches AUTO_BATCH_SIZE in src/api/router.js — background mode's batch
// size isn't configurable from here, it always runs at the server's cap.
const AUTO_BATCH_SIZE = 20;

export default function SowLegacyExtractionPage() {
  const { toast } = useToast();
  const [tab, setTab] = useState('extract'); // 'extract' | 'summary' | 'match'
  const [status, setStatus]     = useState(null);
  const [batchSize, setBatchSize] = useState(5);
  const [model, setModel]       = useState('claude-sonnet-4-6');
  const [clearingHaiku, setClearingHaiku] = useState(false);
  const [targetRefsInput, setTargetRefsInput] = useState('');
  const [retryFailed, setRetryFailed] = useState(false);
  const [running, setRunning]   = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const autoRunRef = useRef(false); // avoids stale-closure issues stopping the loop from a click handler
  const [resetting, setResetting] = useState(false);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [log, setLog]           = useState([]); // per-run results, newest first
  const [results, setResults]   = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  // ── Per-SOW summary (which SOWs are worth reprocessing/investigating) ──
  const [summary, setSummary]           = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFilter, setSummaryFilter] = useState('attention'); // 'attention' | 'all'
  const [summarySearch, setSummarySearch] = useState('');

  const loadSummary = useCallback(() => {
    setSummaryLoading(true);
    api.get('sow-legacy-extraction/summary')
      .then(r => setSummary(r.summary || []))
      .catch(() => setSummary([]))
      .finally(() => setSummaryLoading(false));
  }, []);

  useEffect(() => { if (tab === 'summary') loadSummary(); }, [tab, loadSummary]);

  // Jumps to the Extract tab with this SOW pre-filled for a targeted retry.
  const reprocessSow = (reference) => {
    setTargetRefsInput(reference);
    setRetryFailed(true);
    setTab('extract');
  };

  const loadStatus = useCallback(() => api.get('sow-legacy-extraction/status').then(setStatus).catch(() => {}), []);
  const loadResults = useCallback(() => {
    setResultsLoading(true);
    api.get('sow-legacy-extraction/results', { limit: 500 })
      .then(r => setResults(r.results || []))
      .catch(() => setResults([]))
      .finally(() => setResultsLoading(false));
  }, []);

  useEffect(() => { loadStatus(); loadResults(); }, [loadStatus, loadResults]);

  // While the server-side background job is on, poll for progress instead of
  // requiring a manual refresh — this is the whole point of it (works even if
  // this page/tab isn't open at all; polling here is just so an open tab
  // reflects what the cron job is doing without a page reload).
  useEffect(() => {
    if (!status?.auto_enabled) return;
    const id = setInterval(() => { loadStatus(); loadResults(); }, 20000);
    return () => clearInterval(id);
  }, [status?.auto_enabled, loadStatus, loadResults]);

  const toggleAuto = async () => {
    setTogglingAuto(true);
    try {
      const enabling = !status?.auto_enabled;
      const r = await api.post('sow-legacy-extraction/auto', { enabled: enabling, model });
      setStatus(prev => ({ ...prev, auto_enabled: r.auto_enabled }));
      toast.success(enabling
        ? `Background processing on — starting a batch of ${r.batch_size} right now, then every 2 minutes after that, even with this tab closed. Refresh in ~20s to see it land.`
        : 'Background processing off.');
    } catch (e) {
      toast.error(e.message || 'Failed to update background processing');
    }
    setTogglingAuto(false);
  };

  // Runs one bounded batch against the API and folds the result into the log —
  // shared by the single-click button and the auto-run loop below.
  const runOneBatch = async () => {
    try {
      const references = targetRefsInput.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
      const r = await api.post('sow-legacy-extraction/run', {
        limit: batchSize, model,
        ...(references.length ? { references, retry_failed: retryFailed } : {}),
      });
      if (r.results.length) setLog(prev => [...r.results, ...prev]);
      await loadStatus();
      return r;
    } catch (e) {
      toast.error(e.message || 'Batch failed');
      return null;
    }
  };

  const runBatch = async () => {
    setRunning(true);
    const r = await runOneBatch();
    if (r) {
      if (!r.results.length) {
        const references = targetRefsInput.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
        toast.error(references.length
          ? 'None of those SOWs had an unprocessed SOW document to extract — check the reference(s), or tick "Retry" if they already have a previous attempt.'
          : 'Nothing left to process.');
      } else {
        const ok = r.results.filter(x => x.success).length;
        const fail = r.results.filter(x => !x.success).length;
        const cost = r.results.reduce((a, x) => a + (x.cost_usd || 0), 0);
        toast[fail ? 'error' : 'success'](
          `Processed ${r.processed}: ${ok} extracted${fail ? `, ${fail} failed` : ''} (~$${cost.toFixed(3)} this batch).`
        );
      }
    }
    await loadResults();
    setRunning(false);
  };

  // Keeps calling runOneBatch back-to-back until nothing's left or Cancel is
  // hit. Still goes through the same bounded per-call endpoint underneath —
  // this just automates clicking "Extract next N" so a large backlog doesn't
  // need babysitting, without changing the safety/cost-visibility model.
  const runAll = async () => {
    autoRunRef.current = true;
    setAutoRunning(true);
    setRunning(true);
    let batches = 0, ok = 0, fail = 0, cost = 0;
    while (autoRunRef.current) {
      const r = await runOneBatch();
      if (!r || !r.results.length) break;
      batches++;
      ok   += r.results.filter(x => x.success).length;
      fail += r.results.filter(x => !x.success).length;
      cost += r.results.reduce((a, x) => a + (x.cost_usd || 0), 0);
      await new Promise(res => setTimeout(res, 300)); // brief pause between calls, keeps the UI responsive
    }
    await loadResults();
    const stopped = !autoRunRef.current;
    autoRunRef.current = false;
    setAutoRunning(false);
    setRunning(false);
    if (batches === 0) toast.error('Nothing left to process.');
    else toast[fail ? 'error' : 'success'](
      `${stopped ? 'Stopped' : 'Finished'} after ${batches} batch${batches === 1 ? '' : 'es'}: ${ok} extracted${fail ? `, ${fail} failed` : ''} (~$${cost.toFixed(3)} total).`
    );
  };

  const cancelAutoRun = () => { autoRunRef.current = false; };

  const resetAll = async () => {
    if (!confirm(
      `Clear ALL ${status?.processed_documents ?? 0} processed extraction(s) so every SOW document is picked up again?\n\n` +
      `This deletes the staged line items only — nothing was ever written to the live commercial tables or product catalogue, ` +
      `and the ${formatMoney((status?.total_cost_usd || 0) * 0.79)} already spent isn't affected. This can't be undone.`
    )) return;
    setResetting(true);
    try {
      const r = await api.post('sow-legacy-extraction/reset', {});
      toast.success(`Cleared ${r.deleted} line item${r.deleted === 1 ? '' : 's'} — ready to reprocess from scratch.`);
      setLog([]);
      await loadStatus();
      await loadResults();
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    }
    setResetting(false);
  };

  const resetListed = async () => {
    const references = targetRefsInput.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    if (!references.length) return;
    if (!confirm(`Clear any existing extraction for ${references.length} listed SOW(s) so they can be reprocessed?`)) return;
    setResetting(true);
    try {
      const r = await api.post('sow-legacy-extraction/reset', { references });
      toast.success(`Cleared ${r.deleted} line item${r.deleted === 1 ? '' : 's'} for the listed SOW(s).`);
      await loadStatus();
      await loadResults();
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    }
    setResetting(false);
  };

  // Haiku frequently extracts the right numbers (days, day rate) but misses
  // the actual role/band name for resource lines, defaulting to a generic
  // "Resource" — Sonnet is far more reliable at this. Clears only the
  // documents that were actually run through Haiku (tracked via
  // ai_usage_log, not by re-reading every row) so nothing already done well
  // needs reprocessing.
  const haikuCount = status?.by_model?.['claude-haiku-4-5'] || 0;
  const clearHaikuExtractions = async () => {
    if (!confirm(
      `Clear the ${haikuCount} document${haikuCount === 1 ? '' : 's'} extracted with Haiku so they're picked up again ` +
      `(next run will use whichever model is selected above — Sonnet is recommended)?\n\nThis only deletes the staged ` +
      `line items for those documents; nothing was ever written to the live commercial tables or product catalogue, ` +
      `and past spend isn't affected. This can't be undone.`
    )) return;
    setClearingHaiku(true);
    try {
      const r = await api.post('sow-legacy-extraction/reset', { model: 'claude-haiku-4-5' });
      toast.success(`Cleared ${r.deleted} line item${r.deleted === 1 ? '' : 's'} from Haiku-extracted documents — ready to reprocess with Sonnet.`);
      setLog([]);
      await loadStatus();
      await loadResults();
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    }
    setClearingHaiku(false);
  };

  const exportResults = () => {
    const header = ['SOW', 'Type', 'Description', 'Band/Category', 'Quantity', 'Unit', 'Unit Price', 'Line Total', 'Confidence', 'Raw Text'];
    const rows = results.map(r => [
      r.sow_reference, r.line_type, r.description, r.band_or_category || '',
      r.quantity ?? '', r.unit || '', r.unit_price ?? '', r.line_total ?? '', r.extraction_confidence || '', r.raw_text || '',
    ]);
    downloadCsv('sow-legacy-line-items.csv', header, rows);
  };

  const remaining = status ? status.total_documents - status.processed_documents : null;
  const failedCount = results.filter(r => (r.description || '').startsWith('[EXTRACTION FAILED')).length;

  // ── Match to product catalogue ──────────────────────────────────────────
  const [catalog, setCatalog]         = useState([]);
  // Resource lines match against the active rate card's bands, not the
  // product catalogue — resource pricing is driven by rate cards everywhere
  // else in the app (product_catalog's category='Resource' rows are
  // unrelated old seed data). Shaped {id, code, description} like a catalog
  // item so the same search/guess helpers work for both.
  const [bands, setBands]             = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [building, setBuilding]       = useState(false);
  const [matched, setMatched]         = useState([]);
  const [clusters, setClusters]       = useState([]);
  // Per-row/cluster pending decision, keyed by matched row id or cluster's first line_id
  const [decisions, setDecisions]     = useState({});
  const [submittingMatches, setSubmittingMatches] = useState(false);
  const [readyToCommit, setReadyToCommit] = useState(0);
  const [committing, setCommitting]   = useState(false);

  const loadCatalog = useCallback(() => api.get('product-catalog').then(d => setCatalog(Array.isArray(d) ? d : [])).catch(() => {}), []);
  const loadBands = useCallback(() => api.get('rate-cards/active-bands')
    .then(d => setBands(Array.isArray(d) ? d.map(b => ({ id: b.id, code: b.band_code, description: b.band_name })) : []))
    .catch(() => setBands([])), []);
  const loadMatchReview = useCallback(() => {
    setMatchLoading(true);
    api.get('sow-legacy-extraction/match/review')
      .then(d => { setMatched(d.matched || []); setClusters(d.clusters || []); setReadyToCommit(d.ready_to_commit || 0); })
      .catch(() => { setMatched([]); setClusters([]); setReadyToCommit(0); })
      .finally(() => setMatchLoading(false));
  }, []);

  useEffect(() => { if (tab === 'match') { loadCatalog(); loadBands(); loadMatchReview(); } }, [tab, loadCatalog, loadBands, loadMatchReview]);

  const buildMatches = async () => {
    setBuilding(true);
    try {
      const r = await api.post('sow-legacy-extraction/match/build', {});
      toast.success(`Checked ${r.processed} line item${r.processed === 1 ? '' : 's'}: ${r.matched} matched an existing product, ${r.unmatched} didn't.`);
      await loadMatchReview();
    } catch (e) {
      toast.error(e.message || 'Match build failed');
    }
    setBuilding(false);
  };

  const setDecision = (key, patch) => setDecisions(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const submitMatchDecisions = async () => {
    const payload = Object.entries(decisions)
      .filter(([, d]) => d?.action)
      .map(([, d]) => ({
        ids: d.ids, line_type: d.line_type, action: d.action,
        product_catalog_id: d.product_catalog_id, rate_card_band_id: d.rate_card_band_id, new_product: d.new_product,
      }));
    if (!payload.length) { toast.error('No decisions made yet.'); return; }
    setSubmittingMatches(true);
    try {
      const r = await api.post('sow-legacy-extraction/match/resolve', { decisions: payload });
      const ok = r.results.filter(x => x.success).length;
      const failures = r.results.filter(x => !x.success);
      // Deliberately not done yet — resolve only stages the decision
      // (candidate_product_catalog_id + reviewed=1). Nothing shows on the
      // SOW or the catalogue item until "Commit to SOWs" below is clicked
      // too, which is easy to miss after seeing a success toast here.
      // Surface the real error rather than just a count — a plain "N
      // failed" with no reason means every failure has to be chased down
      // by reading server code instead of the message right in front of you.
      let msg = `Applied ${ok} decision${ok === 1 ? '' : 's'}.`;
      if (failures.length) msg += ` ${failures.length} failed: ${[...new Set(failures.map(f => f.error))].join('; ')}`;
      if (ok) msg += ' Nothing is written to the SOW yet — click "Commit to SOWs" below to finish.';
      toast[failures.length ? 'error' : 'success'](msg);
      setDecisions({});
      await loadMatchReview();
    } catch (e) {
      toast.error(e.message || 'Failed to apply decisions');
    }
    setSubmittingMatches(false);
  };

  // Extraction sometimes buckets a line as the wrong type (e.g. a resource
  // role mis-tagged as hardware). Flips it server-side and clears any
  // existing match (it was scored against the wrong pool), then reloads —
  // the item reappears immediately as a fresh, unmatched candidate of the
  // new type.
  const reclassify = async (ids, newType) => {
    try {
      await api.post('sow-legacy-extraction/match/reclassify', { ids, line_type: newType });
      toast.success(`Reclassified as ${newType}. Re-run "Check for catalogue matches" to score it against the right pool.`);
      await loadMatchReview();
    } catch (e) {
      toast.error(e.message || 'Reclassify failed');
    }
  };

  // Writes every confirmed/created match into the SOW's own commercial
  // lines (sow_hardware_lines/sow_resource_lines) — cheap, no AI calls, so
  // just loop the bounded endpoint until nothing's left rather than
  // exposing a cancel/batch-size control like the AI extraction step does.
  const commitToSows = async () => {
    if (!readyToCommit) return;
    if (!confirm(
      `Write ${readyToCommit} confirmed line${readyToCommit === 1 ? '' : 's'} into their SOWs' commercial lines? ` +
      `This makes them show on each SOW's Commercial tab and in Product Sale History, and updates each SOW's total ` +
      `to reflect its real line items. Only the historical sell price was extractable — there's no cost basis for ` +
      `legacy hardware, so margin will show as ~100% until real cost data is added separately.`
    )) return;
    setCommitting(true);
    let committed = 0, hardware = 0, resources = 0, sowsUpdated = 0;
    try {
      let more = true;
      while (more) {
        const r = await api.post('sow-legacy-extraction/commit', {});
        committed += r.committed || 0;
        hardware += r.hardware || 0;
        resources += r.resources || 0;
        sowsUpdated += r.sows_updated || 0;
        more = !!r.more_remaining && r.committed > 0;
      }
      toast.success(`Committed ${committed} line${committed === 1 ? '' : 's'} (${resources} resource, ${hardware} hardware) across ${sowsUpdated} SOW${sowsUpdated === 1 ? '' : 's'}.`);
      await loadMatchReview();
    } catch (e) {
      toast.error(e.message || 'Commit failed');
    }
    setCommitting(false);
  };

  // Manual refresh for the current tab's data — mobile browsers throttle
  // background tabs, so the 20s auto-poll (and the auto-run loop) can go
  // stale without this; previously the only fix was navigating away and back.
  const [refreshingAll, setRefreshingAll] = useState(false);
  const refreshAll = async () => {
    setRefreshingAll(true);
    try {
      await Promise.all([
        loadStatus(),
        tab === 'extract' && loadResults(),
        tab === 'summary' && loadSummary(),
        tab === 'match' && loadMatchReview(),
      ].filter(Boolean));
    } finally {
      setRefreshingAll(false);
    }
  };

  return (
    <div className="fade-in p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-base font-extrabold text-navy">SOW Legacy Line-Item Extraction</h1>
          <div className="text-[9px] text-gray-500 mt-0.5">
            For legacy SOWs, hardware and resource pricing only exists inside the issued document itself — there's no
            structured data to read (see Product Sale History for SOWs that already have it). This uses AI to read each
            attached SOW document and pull out every priced line item into a staging table below — nothing is written to
            the live commercial tables or the product catalogue; merging into the catalogue is a separate, later step
            once you've reviewed what came out.
          </div>
        </div>
        <button onClick={refreshAll} disabled={refreshingAll}
          className="px-2.5 py-1.5 text-[9px] font-bold border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-40 flex-shrink-0 whitespace-nowrap">
          {refreshingAll ? 'Refreshing…' : '⟳ Refresh'}
        </button>
      </div>

      <div className="flex border border-gray-200 rounded overflow-hidden mb-4 w-fit">
        {[['extract', 'Extract'], ['summary', 'Per-SOW Summary'], ['match', 'Match to Catalogue']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-3 py-1.5 text-[9px] font-bold ${tab === key ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'extract' && <>
      <div className={`border rounded p-3 mb-4 flex items-center justify-between gap-3 flex-wrap ${status?.auto_enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
        <div className="text-[9px] text-gray-600 max-w-2xl">
          <strong className={status?.auto_enabled ? 'text-emerald-700' : 'text-navy'}>
            {status?.auto_enabled ? '● Background processing is ON' : 'Background processing is off'}
          </strong>{' '}
          — runs a batch of {AUTO_BATCH_SIZE} ({MODEL_OPTIONS.find(o => o.value === model)?.label.split(' (')[0]}) as soon as you turn
          it on, then every 2 minutes after that, whether or not this page — or your browser — is open. That's up to
          ~{AUTO_BATCH_SIZE * 30} documents/hour (self-throttled if it hits your Anthropic account's rate limit) — the fastest
          way to clear a large backlog unattended, e.g. while you're away from a screen for a few hours. Cost still logs the
          same way and shows up below on refresh.
        </div>
        <button onClick={toggleAuto} disabled={togglingAuto}
          className={`px-3 py-1.5 text-[10px] font-bold rounded disabled:opacity-40 flex-shrink-0 ${
            status?.auto_enabled ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
          {togglingAuto ? 'Saving…' : status?.auto_enabled ? 'Turn off' : `Turn on (${AUTO_BATCH_SIZE}/~2min)`}
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 text-[9px] text-amber-800">
        <strong>Cost stays visible throughout.</strong> "Process all remaining" below automates clicking through
        batches of {batchSize} while this tab stays open and awake — it's cancellable at any point, and everything
        already processed is skipped if you stop and come back later. Use "Just next {batchSize}" instead if you'd
        rather review cost/quality one batch at a time, or use "Turn on" above for hands-off server-side processing.
      </div>

      <div className="bg-white border border-gray-200 rounded p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="text-[10px] font-bold text-navy">
            {status ? (
              <>
                <strong>{status.processed_documents}</strong> of <strong>{status.total_documents}</strong> SOW documents processed
                {remaining > 0 && <span className="text-gray-400 font-normal"> — {remaining} remaining</span>}
              </>
            ) : 'Loading…'}
          </div>
          <div className="text-[9px] text-gray-500">
            Total spend so far: <strong className="text-navy">{formatMoney((status?.total_cost_usd || 0) * 0.79)}</strong>{' '}
            <span className="text-gray-400">(${(status?.total_cost_usd || 0).toFixed(3)})</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-[9px] text-gray-500">Batch size</label>
          <select value={batchSize} onChange={e => setBatchSize(parseInt(e.target.value, 10))} disabled={running}
            className="form-input text-[9px] py-0.5 w-16">
            {[3, 5, 10, 20].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <label className="text-[9px] text-gray-500 ml-2">Model</label>
          <select value={model} onChange={e => setModel(e.target.value)} disabled={running}
            className="form-input text-[9px] py-0.5">
            {MODEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {autoRunning ? (
            <button onClick={cancelAutoRun}
              className="px-3 py-1.5 text-[10px] font-bold bg-red-600 text-white rounded hover:bg-red-700 ml-auto">
              ■ Cancel — {status?.processed_documents ?? 0} of {status?.total_documents ?? 0} done so far
            </button>
          ) : targetRefsInput.trim() ? (
            <button onClick={runBatch} disabled={running}
              className="px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40 ml-auto">
              {running ? 'Extracting…' : `Extract ${targetRefsInput.split(/[\s,]+/).filter(Boolean).length} listed SOW(s)`}
            </button>
          ) : (
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={runBatch} disabled={running || remaining === 0}
                className="px-2 py-1.5 text-[9px] font-semibold border border-gray-200 rounded text-gray-500 hover:bg-gray-50 disabled:opacity-40">
                Just next {batchSize}
              </button>
              <button onClick={runAll} disabled={running || remaining === 0}
                className="px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
                {remaining === 0 ? 'All documents processed' : `Process all ${remaining} remaining`}
              </button>
            </div>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100">
          <label className="text-[9px] font-semibold text-gray-500 block mb-1">
            Test specific SOWs instead of oldest-first <span className="font-normal text-gray-400">(optional — comma or newline separated references, e.g. AV-402300, AV-402315)</span>
          </label>
          <textarea value={targetRefsInput} onChange={e => setTargetRefsInput(e.target.value)} disabled={running} rows={2}
            placeholder="Leave blank to keep working through the oldest unprocessed SOW documents in order…"
            className="form-input text-[9px] w-full" />
          {targetRefsInput.trim() && (
            <div className="flex items-center justify-between mt-1.5 flex-wrap gap-2">
              <label className="flex items-center gap-1.5 text-[9px] text-gray-500">
                <input type="checkbox" checked={retryFailed} onChange={e => setRetryFailed(e.target.checked)} disabled={running} />
                Retry — clear any previous attempt for these SOWs first (successful or failed), then reprocess
              </label>
              <button onClick={resetListed} disabled={resetting || running}
                className="text-[9px] font-semibold text-red-500 hover:text-red-700 disabled:opacity-40">
                Just clear their data (don't reprocess)
              </button>
            </div>
          )}
        </div>

        {haikuCount > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap bg-amber-50/60 -mx-4 -mb-4 px-4 pb-4">
            <div className="text-[8px] text-amber-800">
              <strong>{haikuCount} document{haikuCount === 1 ? '' : 's'}</strong> {haikuCount === 1 ? 'was' : 'were'} extracted with Haiku,
              which often misses the resource role name (shows generic "Resource" instead of e.g. "Technical Specialist").
              Clear just those to reprocess with Sonnet — no need to redo documents already done well.
            </div>
            <button onClick={clearHaikuExtractions} disabled={clearingHaiku || running}
              className="px-2 py-1 text-[9px] font-bold border border-amber-300 text-amber-700 bg-white rounded hover:bg-amber-100 disabled:opacity-40 flex-shrink-0">
              {clearingHaiku ? 'Clearing…' : `Clear ${haikuCount} Haiku extraction${haikuCount === 1 ? '' : 's'} for reprocessing`}
            </button>
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <div className="text-[8px] text-gray-400">
            Made a bad batch (e.g. under a bug that's since been fixed)? Wipe the staged results and start over —
            nothing here has ever touched the live commercial tables or product catalogue.
          </div>
          <button onClick={resetAll} disabled={resetting || running || !status?.processed_documents}
            className="px-2 py-1 text-[9px] font-bold border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-40 flex-shrink-0 ml-3">
            {resetting ? 'Clearing…' : 'Reset all — clear every extracted document'}
          </button>
        </div>
      </div>

      {log.length > 0 && (
        <div className="bg-white border border-gray-200 rounded mb-4">
          <div className="px-3 py-2 border-b border-gray-200 text-[9px] font-bold text-gray-500 uppercase">Recent batches</div>
          <table className="w-full">
            <tbody>
              {log.slice(0, 40).map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="px-3 py-1 text-[9px] font-semibold text-navy whitespace-nowrap">{r.sow_reference}</td>
                  <td className="px-2 py-1 text-[9px]">
                    {r.success
                      ? <span className="text-emerald-600">✓ {r.resources} resource, {r.hardware} hardware line{(r.resources + r.hardware) === 1 ? '' : 's'}</span>
                      : <span className="text-red-500">✗ {r.error}</span>}
                  </td>
                  <td className="px-2 py-1 text-[9px] text-gray-400 text-right">{r.cost_usd != null ? `$${r.cost_usd.toFixed(4)}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
          <div className="text-[9px] font-bold text-navy uppercase">
            Extracted line items {resultsLoading ? '' : `(${results.length})`}
            {failedCount > 0 && <span className="ml-2 text-red-500 font-semibold">{failedCount} failed extractions</span>}
          </div>
          <button onClick={exportResults} disabled={!results.length}
            className="px-2 py-1 text-[9px] font-bold border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-40">
            ⬇ Export CSV
          </button>
        </div>
        {resultsLoading ? <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div> : !results.length ? (
          <div className="text-center py-8 text-[10px] text-gray-400">No line items extracted yet — run a batch above.</div>
        ) : (
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">SOW</th>
                  <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Type</th>
                  <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Description</th>
                  <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Qty</th>
                  <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Unit</th>
                  <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Unit Price</th>
                  <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Total</th>
                  <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => {
                  const failed = (r.description || '').startsWith('[EXTRACTION FAILED');
                  return (
                    <tr key={r.id} className={`border-t border-gray-100 ${failed ? 'bg-red-50/50' : ''}`} title={r.raw_text || ''}>
                      <td className="px-2 py-1 text-[9px] font-semibold text-navy whitespace-nowrap">{r.sow_reference}</td>
                      <td className="px-2 py-1 text-[9px]">
                        {failed ? <span className="text-red-500 font-bold">FAILED</span> :
                          <span className={r.line_type === 'resource' ? 'text-blue-600' : 'text-purple-600'}>{r.line_type}</span>}
                      </td>
                      <td className="px-2 py-1 text-[9px] text-gray-700 max-w-[280px]">{r.description}</td>
                      <td className="px-2 py-1 text-[9px] text-right">{r.quantity ?? '—'}</td>
                      <td className="px-2 py-1 text-[9px] text-gray-500">{r.unit || '—'}</td>
                      <td className="px-2 py-1 text-[9px] text-right">{r.unit_price != null ? formatMoney(r.unit_price) : '—'}</td>
                      <td className="px-2 py-1 text-[9px] text-right font-medium">{r.line_total != null ? formatMoney(r.line_total) : '—'}</td>
                      <td className="px-2 py-1 text-[9px]">
                        {r.extraction_confidence && (
                          <span className={`px-1 py-0.5 rounded text-[8px] font-bold ${
                            r.extraction_confidence === 'high' ? 'bg-emerald-100 text-emerald-700'
                            : r.extraction_confidence === 'low' ? 'bg-red-100 text-red-600'
                            : 'bg-amber-100 text-amber-700'}`}>{r.extraction_confidence}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>}

      {tab === 'summary' && (
        <SummaryTab
          summary={summary}
          summaryLoading={summaryLoading}
          summaryFilter={summaryFilter}
          setSummaryFilter={setSummaryFilter}
          summarySearch={summarySearch}
          setSummarySearch={setSummarySearch}
          reprocessSow={reprocessSow}
          refresh={loadSummary}
        />
      )}

      {tab === 'match' && (
        <MatchToCatalogTab
          catalog={catalog}
          bands={bands}
          matchLoading={matchLoading}
          building={building}
          matched={matched}
          clusters={clusters}
          decisions={decisions}
          setDecision={setDecision}
          buildMatches={buildMatches}
          submitMatchDecisions={submitMatchDecisions}
          submittingMatches={submittingMatches}
          reclassify={reclassify}
          readyToCommit={readyToCommit}
          commitToSows={commitToSows}
          committing={committing}
        />
      )}
    </div>
  );
}

// ── Per-SOW summary tab ─────────────────────────────────────────────────────
// A row's status: "failed" if any line is an [EXTRACTION FAILED...] marker,
// "empty" if it extracted "successfully" but found zero priced items (a
// scanned/blank doc, or the model came back empty), otherwise "ok".
function sowSummaryStatus(row) {
  if (Number(row.failed_count) > 0) return 'failed';
  if (Number(row.resource_count) === 0 && Number(row.hardware_count) === 0) return 'empty';
  return 'ok';
}

// Reconciles the sum of extracted lines against the SOW's own known value
// (total_excl_vat — the figure that existed before any of this extraction
// ran, since legacy SOWs never had real resource/hardware lines before
// now). A SOW can extract a plausible handful of lines that still fall well
// short of what it was actually worth — this catches that even when the
// line-level status above looks "ok".
const VALUE_MISMATCH_THRESHOLD = 0.15; // 15% gap
function sowValueCheck(row) {
  const sowValue = Number(row.sow_value) || 0;
  const extracted = Number(row.extracted_total) || 0;
  if (sowValue <= 0) return { checkable: false };
  const variance = extracted - sowValue;
  const variancePct = variance / sowValue;
  return { checkable: true, sowValue, extracted, variance, variancePct, mismatch: Math.abs(variancePct) > VALUE_MISMATCH_THRESHOLD };
}

function SummaryTab({ summary, summaryLoading, summaryFilter, setSummaryFilter, summarySearch, setSummarySearch, reprocessSow, refresh }) {
  const withStatus = summary.map(r => ({ ...r, status: sowSummaryStatus(r), valueCheck: sowValueCheck(r) }));
  const needsAttention = r => r.status !== 'ok' || r.valueCheck.mismatch;
  const attentionCount = withStatus.filter(needsAttention).length;
  const search = summarySearch.trim().toUpperCase();
  const rows = withStatus
    .filter(r => summaryFilter === 'all' || needsAttention(r))
    .filter(r => !search || r.sow_reference?.toUpperCase().includes(search) || r.sow_detail?.toUpperCase().includes(search));

  const STATUS_BADGE = {
    ok:     'bg-emerald-100 text-emerald-700',
    empty:  'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-600',
  };
  const STATUS_LABEL = { ok: 'OK', empty: 'EMPTY', failed: 'FAILED' };

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-[9px] text-blue-800">
        One row per SOW that's had an extraction attempt. <strong>FAILED</strong> means the document either couldn't be
        read (bad file, unreadable format, scanned image with no text) or the extraction call itself errored (rate
        limited, Claude API error) — hover the badge to see which, or the <span className="text-gold font-bold">↻</span> mark
        means it's worth reprocessing rather than a permanent file problem. <strong>EMPTY</strong> means it read fine but
        the model found zero priced resource or hardware lines. <strong>Value check</strong> compares the sum of
        everything extracted against the SOW's own known value — a big gap means something was likely missed,
        duplicated, or mis-priced even if line counts look fine. Use "Reprocess" to clear and retry a SOW with the
        model/settings on the Extract tab.
      </div>

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[9px] text-gray-500">
          <strong className="text-navy">{summary.length}</strong> SOW{summary.length === 1 ? '' : 's'} processed ·{' '}
          <strong className={attentionCount ? 'text-red-500' : 'text-emerald-600'}>{attentionCount}</strong> need attention
        </div>
        <div className="flex items-center gap-2">
          <input value={summarySearch} onChange={e => setSummarySearch(e.target.value)} placeholder="Search SOW reference/detail…"
            className="form-input text-[9px] py-1 w-48" />
          <div className="flex border border-gray-200 rounded overflow-hidden">
            {[['attention', `Needs attention (${attentionCount})`], ['all', 'All']].map(([key, label]) => (
              <button key={key} onClick={() => setSummaryFilter(key)}
                className={`px-2 py-1 text-[9px] font-bold ${summaryFilter === key ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={refresh} className="px-2 py-1 text-[9px] font-bold border border-gray-200 rounded bg-white hover:bg-gray-50">
            ⟳ Refresh
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        {summaryLoading ? (
          <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-[10px] text-gray-400">
            {summary.length === 0 ? 'No SOWs processed yet.' : 'Nothing matches this filter — try "All".'}
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">SOW</th>
                  <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Detail</th>
                  <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Resources</th>
                  <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Hardware</th>
                  <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Status</th>
                  <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">SOW Value</th>
                  <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold">Extracted</th>
                  <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Value Check</th>
                  <th className="px-2 py-1.5 text-right text-[8px] uppercase text-gray-500 font-bold"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.sow_id} className="border-t border-gray-100">
                    <td className="px-2 py-1.5 text-[9px] font-semibold text-navy whitespace-nowrap">{r.sow_reference}</td>
                    <td className="px-2 py-1.5 text-[9px] text-gray-500 max-w-[220px] truncate">{r.sow_detail || '—'}</td>
                    <td className="px-2 py-1.5 text-[9px] text-right">{r.resource_count}</td>
                    <td className="px-2 py-1.5 text-[9px] text-right">{r.hardware_count}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                      {r.status === 'failed' && r.failed_reasons?.length > 0 && (
                        <div
                          className={`mt-0.5 text-[8px] font-semibold cursor-help ${r.failed_reasons.some(fr => fr.retryable) ? 'text-gold' : 'text-gray-400'}`}
                          title={r.failed_reasons.map(fr => fr.message).join('\n')}
                        >
                          {r.failed_reasons.some(fr => fr.retryable) ? '↻ worth retrying' : '⊘ unreadable file'}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-[9px] text-right">{r.valueCheck.checkable ? formatMoney(r.valueCheck.sowValue) : '—'}</td>
                    <td className="px-2 py-1.5 text-[9px] text-right">{formatMoney(r.extracted_total)}</td>
                    <td className="px-2 py-1.5">
                      {!r.valueCheck.checkable ? (
                        <span className="text-[8px] text-gray-300">no SOW value on file</span>
                      ) : (
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${r.valueCheck.mismatch ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>
                          {r.valueCheck.mismatch
                            ? `${r.valueCheck.variance > 0 ? '+' : ''}${Math.round(r.valueCheck.variancePct * 100)}%`
                            : 'MATCH'}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {needsAttention(r) && (
                        <button onClick={() => reprocessSow(r.sow_reference)} className="text-[9px] font-semibold text-gold hover:underline">
                          Reprocess
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Match to Catalogue tab ───────────────────────────────────────────────────
const HIGH_CONFIDENCE_THRESHOLD = 0.9;

function MatchToCatalogTab({ catalog, bands, matchLoading, building, matched, clusters, decisions, setDecision, buildMatches, submitMatchDecisions, submittingMatches, reclassify, readyToCommit, commitToSows, committing }) {
  const decidedCount = Object.values(decisions).filter(d => d?.action).length;
  const pendingCount = matched.length + clusters.length;
  const [linkPickerKey, setLinkPickerKey] = useState(null);
  const [linkQuery, setLinkQuery] = useState('');

  // One best-guess per cluster, computed once (not per keystroke/re-render) —
  // with 2000+ candidates, reviewing each one-by-one to spot the obvious
  // matches is the real bottleneck. Anything scoring >=90% gets surfaced as
  // a one-click recommendation instead of requiring "Link existing" to be
  // opened just to see what the system already suspects.
  const bestGuesses = useMemo(() => {
    const hwCatalog = catalog.filter(x => x.category !== 'Resource');
    const hwTokens = new Map(hwCatalog.map(c => [c.id, fmTokens(c.description)]));
    const bandTokens = new Map(bands.map(b => [b.id, fmTokens(b.description)]));
    const map = new Map();
    for (const c of clusters) {
      const scoped = c.line_type === 'resource' ? bands : hwCatalog;
      const tokensMap = c.line_type === 'resource' ? bandTokens : hwTokens;
      const [top] = topCatalogGuesses(c.representative_description, scoped, 1, tokensMap);
      if (top) map.set(c.line_ids[0], top);
    }
    return map;
  }, [clusters, catalog, bands]);
  const highConfidenceKeys = useMemo(() =>
    clusters
      .map(c => c.line_ids[0])
      .filter(key => (bestGuesses.get(key)?.score || 0) >= HIGH_CONFIDENCE_THRESHOLD && decisions[key]?.action !== 'confirm'),
    [clusters, bestGuesses, decisions]
  );
  const autoSelectHighConfidence = () => {
    for (const key of highConfidenceKeys) {
      const c = clusters.find(cl => cl.line_ids[0] === key);
      const g = bestGuesses.get(key);
      if (!c || !g) continue;
      const idKey = c.line_type === 'resource' ? 'rate_card_band_id' : 'product_catalog_id';
      setDecision(key, { ids: c.line_ids, line_type: c.line_type, action: 'confirm', [idKey]: g.item.id });
    }
  };

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-[9px] text-blue-800">
        Matches every not-yet-reviewed extracted line against its right source — hardware against the product
        catalogue, resources against the active rate card's bands (resource pricing lives in rate cards everywhere
        else in the app, not the catalogue). Most legacy lines won't match anything yet — that's expected, since this
        is how the catalogue gets built up. Lines that don't match anything are grouped with other lines that look
        like the same real item extracted from different SOWs, so you get one prompt instead of one per SOW. If a
        "new item" candidate actually already exists under different wording, use <strong>🔎 Link existing</strong>
        (hardware) or <strong>🔎 Choose band</strong> (resources) to search instead of creating a duplicate — resource
        lines only ever link to one of the existing bands, never create a new one. <strong>Nothing is written to the
        catalogue, rate cards, or any SOW's commercial lines, until you submit decisions and then commit below.</strong>
      </div>

      {readyToCommit > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[9px] text-emerald-800">
            <strong>{readyToCommit}</strong> confirmed line{readyToCommit === 1 ? '' : 's'} ready to commit — write these into
            their SOWs' Commercial tab (and Product Sale History) using the real historical price/role name extracted
            for each one, not the catalogue's current price.
          </div>
          <button onClick={commitToSows} disabled={committing}
            className="px-3 py-1.5 text-[10px] font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 flex-shrink-0">
            {committing ? 'Committing…' : `Commit ${readyToCommit} to SOWs`}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={buildMatches} disabled={building}
            className="px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
            {building ? 'Checking…' : 'Check for catalogue matches'}
          </button>
          {highConfidenceKeys.length > 0 && (
            <button onClick={autoSelectHighConfidence}
              className="px-3 py-1.5 text-[10px] font-bold bg-amber-500 text-amber-950 rounded hover:bg-amber-600"
              title="Stages a confirm decision for every candidate scoring 90%+ — still requires Apply + Commit below">
              ⚡ Auto-select {highConfidenceKeys.length} match{highConfidenceKeys.length === 1 ? '' : 'es'} ≥90%
            </button>
          )}
        </div>
        {pendingCount > 0 && (
          <button onClick={submitMatchDecisions} disabled={submittingMatches || !decidedCount}
            className="px-3 py-1.5 text-[10px] font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40">
            {submittingMatches ? 'Applying…' : `Apply ${decidedCount} decision${decidedCount === 1 ? '' : 's'}`}
          </button>
        )}
      </div>

      {matchLoading ? (
        <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>
      ) : pendingCount === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-8 text-center text-[10px] text-gray-400">
          Nothing awaiting review — click "Check for catalogue matches" after extracting more documents.
        </div>
      ) : (
        <div className="space-y-4">
          {matched.length > 0 && (
            <div className="bg-white border border-gray-200 rounded">
              <div className="px-3 py-2 border-b border-gray-200 text-[9px] font-bold text-navy uppercase">
                Suggested matches ({matched.length})
              </div>
              <div className="divide-y divide-gray-100">
                {matched.map(m => {
                  const d = decisions[m.id] || {};
                  const isResource = m.line_type === 'resource';
                  const options = isResource ? bands : catalog;
                  const idKey = isResource ? 'rate_card_band_id' : 'product_catalog_id';
                  const currentId = d[idKey] ?? m.candidate_id;
                  return (
                    <div key={m.id} className="px-3 py-2 flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-[220px]">
                        <div className="text-[9px] font-semibold text-navy">{m.description}</div>
                        <div className="text-[8px] text-gray-400">
                          {m.sow_reference} · {m.line_type} · {m.quantity ?? '—'} {m.unit || ''} · {m.unit_price != null ? formatMoney(m.unit_price) : 'no price'}
                        </div>
                      </div>
                      <div className="text-[8px] text-gray-400">→</div>
                      <div className="flex-1 min-w-[220px]">
                        <select value={currentId} onChange={e => setDecision(m.id, { ids: [m.id], line_type: m.line_type, action: 'confirm', [idKey]: e.target.value })}
                          className="form-select text-[9px] w-full">
                          {options.map(c => <option key={c.id} value={c.id}>{c.code} — {c.description}</option>)}
                        </select>
                        <div className="text-[7px] text-amber-600 mt-0.5">{Math.round(m.match_confidence * 100)}% confidence match{isResource ? ' (rate card band)' : ''}</div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => setDecision(m.id, { ids: [m.id], line_type: m.line_type, action: 'confirm', [idKey]: currentId })}
                          className={`px-2 py-1 text-[8px] font-bold rounded ${d.action === 'confirm' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                          ✓ Confirm
                        </button>
                        <button onClick={() => setDecision(m.id, { ids: [m.id], line_type: m.line_type, action: 'reject' })}
                          className={`px-2 py-1 text-[8px] font-bold rounded ${d.action === 'reject' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                          ✕ Not this
                        </button>
                        <button onClick={() => reclassify([m.id], isResource ? 'hardware' : 'resource')}
                          title={`Extracted as ${m.line_type} — flip if that's wrong`}
                          className="px-2 py-1 text-[8px] font-bold rounded bg-gray-50 text-gray-500 hover:bg-gray-100">
                          ↔ Treat as {isResource ? 'hardware' : 'resource'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {clusters.length > 0 && (
            <div className="bg-white border border-gray-200 rounded">
              <div className="px-3 py-2 border-b border-gray-200 text-[9px] font-bold text-navy uppercase">
                New item candidates ({clusters.length})
              </div>
              <div className="divide-y divide-gray-100">
                {clusters.map(c => {
                  const key = c.line_ids[0];
                  const d = decisions[key] || {};
                  const isResource = c.line_type === 'resource';
                  const idKey = isResource ? 'rate_card_band_id' : 'product_catalog_id';
                  const draftDescription = d.new_product?.description ?? c.representative_description;
                  const draftCategory = d.new_product?.category ?? 'Hardware';
                  const scopedOptions = isResource ? bands : catalog.filter(x => x.category !== 'Resource');
                  const linkedItem = d.action === 'confirm' && d[idKey]
                    ? scopedOptions.find(x => x.id === d[idKey]) : null;
                  const pickerOpen = linkPickerKey === key;
                  const guesses = pickerOpen && !linkQuery.trim()
                    ? topCatalogGuesses(c.representative_description, scopedOptions)
                    : [];
                  const searchResults = pickerOpen && linkQuery.trim().length >= 2
                    ? scopedOptions
                        .filter(x => `${x.code} ${x.description}`.toLowerCase().includes(linkQuery.trim().toLowerCase()))
                        .slice(0, 25)
                    : [];
                  const pickItem = (id) => { setDecision(key, { ids: c.line_ids, line_type: c.line_type, action: 'confirm', [idKey]: id }); setLinkPickerKey(null); };
                  return (
                    <div key={key} className="px-3 py-2.5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-navy/10 text-navy uppercase">{c.line_type}</span>
                            <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">seen {c.count}×</span>
                            {c.min_price != null && (
                              <span className="text-[7px] text-gray-400">
                                {c.min_price === c.max_price ? formatMoney(c.min_price) : `${formatMoney(c.min_price)}–${formatMoney(c.max_price)} (avg ${formatMoney(c.avg_price)})`}
                              </span>
                            )}
                          </div>
                          {linkedItem ? (
                            <div className="text-[9px] font-semibold text-emerald-700">🔗 Will link to: {linkedItem.code} — {linkedItem.description}</div>
                          ) : isResource ? (
                            <div className="text-[9px] font-semibold text-navy">{draftDescription}</div>
                          ) : (
                            <input value={draftDescription}
                              onChange={e => setDecision(key, { ids: c.line_ids, line_type: c.line_type, action: d.action || 'create_new', new_product: { ...d.new_product, description: e.target.value, category: draftCategory } })}
                              className="form-input text-[9px] w-full font-semibold" />
                          )}
                          {!linkedItem && bestGuesses.get(key) && bestGuesses.get(key).score >= HIGH_CONFIDENCE_THRESHOLD && (
                            <button
                              onClick={() => setDecision(key, { ids: c.line_ids, line_type: c.line_type, action: 'confirm', [idKey]: bestGuesses.get(key).item.id })}
                              className="mt-1 px-1.5 py-0.5 text-[8px] font-bold rounded bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100">
                              ⚡ {Math.round(bestGuesses.get(key).score * 100)}% match: {bestGuesses.get(key).item.code} — {bestGuesses.get(key).item.description} — Apply
                            </button>
                          )}
                          <div className="text-[7px] text-gray-400 mt-1">
                            From {c.sow_references.length} SOW{c.sow_references.length === 1 ? '' : 's'}: {c.sow_references.slice(0, 6).join(', ')}{c.sow_references.length > 6 ? ` +${c.sow_references.length - 6} more` : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {!linkedItem && !isResource && (
                            <select value={draftCategory}
                              onChange={e => setDecision(key, { ids: c.line_ids, line_type: c.line_type, action: d.action || 'create_new', new_product: { ...d.new_product, description: draftDescription, category: e.target.value } })}
                              className="form-select text-[8px] py-1">
                              {CATEGORY_OPTIONS.filter(cat => cat !== 'Resource').map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                          )}
                          <button
                            onClick={() => { if (pickerOpen) setLinkPickerKey(null); else { setLinkPickerKey(key); setLinkQuery(''); } }}
                            className={`px-2 py-1 text-[8px] font-bold rounded ${pickerOpen || linkedItem ? 'bg-navy text-white' : 'bg-navy/10 text-navy hover:bg-navy/20'}`}>
                            🔎 {linkedItem ? 'Change link' : isResource ? 'Choose band' : 'Link existing'}
                          </button>
                          {!linkedItem && !isResource && (
                            <button onClick={() => setDecision(key, { ids: c.line_ids, line_type: c.line_type, action: 'create_new', new_product: { description: draftDescription, category: draftCategory, sell_price: c.avg_price } })}
                              className={`px-2 py-1 text-[8px] font-bold rounded ${d.action === 'create_new' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                              + Create item
                            </button>
                          )}
                          <button onClick={() => { setDecision(key, { ids: c.line_ids, line_type: c.line_type, action: 'reject' }); if (pickerOpen) setLinkPickerKey(null); }}
                            className={`px-2 py-1 text-[8px] font-bold rounded ${d.action === 'reject' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                            ✕ Ignore
                          </button>
                          <button onClick={() => { reclassify(c.line_ids, isResource ? 'hardware' : 'resource'); if (pickerOpen) setLinkPickerKey(null); }}
                            title={`Extracted as ${c.line_type} — flip if that's wrong`}
                            className="px-2 py-1 text-[8px] font-bold rounded bg-gray-50 text-gray-500 hover:bg-gray-100">
                            ↔ Treat as {isResource ? 'hardware' : 'resource'}
                          </button>
                        </div>
                      </div>

                      {pickerOpen && (
                        <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded">
                          <input autoFocus value={linkQuery} onChange={e => setLinkQuery(e.target.value)}
                            placeholder={isResource ? 'Search rate card bands by code or name…' : 'Search catalogue by code or description…'}
                            className="form-input text-[9px] w-full mb-1.5" />
                          {!linkQuery.trim() && guesses.length > 0 && (
                            <div className="mb-1.5">
                              <div className="text-[7px] uppercase font-bold text-gray-400 mb-0.5">Closest guesses</div>
                              <div className="flex flex-wrap gap-1">
                                {guesses.map(g => (
                                  <button key={g.item.id} onClick={() => pickItem(g.item.id)}
                                    className="px-1.5 py-0.5 text-[8px] rounded bg-white border border-gray-200 hover:border-navy hover:text-navy">
                                    {g.item.code} — {g.item.description} <span className="text-gray-400">({Math.round(g.score * 100)}%)</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {linkQuery.trim().length >= 2 && (
                            <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded bg-white">
                              {searchResults.length === 0 ? (
                                <div className="px-2 py-1.5 text-[8px] text-gray-400">No matches</div>
                              ) : searchResults.map(item => (
                                <button key={item.id} onClick={() => pickItem(item.id)}
                                  className="w-full text-left px-2 py-1 text-[8px] hover:bg-gray-50">
                                  <span className="font-semibold">{item.code}</span> — {item.description}
                                </button>
                              ))}
                            </div>
                          )}
                          <button onClick={() => setLinkPickerKey(null)} className="text-[8px] text-gray-400 hover:text-gray-600 mt-1">Cancel</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
